import { useEffect, useRef, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatQuantity, roundQuantity } from "@/lib/number";

interface QuantityStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  step?: number;
  className?: string;
  disabled?: boolean;
}

export function QuantityStepper({
  value,
  onChange,
  min = 1,
  step = 1,
  className,
  disabled = false,
}: QuantityStepperProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(formatQuantity(value));

  useEffect(() => {
    setDraft(formatQuantity(value));
  }, [value]);

  const commit = (raw: string) => {
    const parsed = Number.parseFloat(raw.replace(",", "."));
    if (Number.isNaN(parsed)) {
      setDraft(formatQuantity(value));
      return;
    }
    const next = roundQuantity(Math.max(min, parsed), 3);
    onChange(next);
    setDraft(formatQuantity(next));
  };

  const applyStep = (delta: number) => {
    const parsed = Number.parseFloat(draft.replace(",", "."));
    const base = Number.isNaN(parsed) ? value : parsed;
    // Os botões sempre pousam num múltiplo do passo: com passo 1, "+" em 2,1 vai para 3 (não 3,1) e
    // "-" vai para 2. Assim a quantidade avança/recua em unidades inteiras, não em frações.
    const grid =
      delta > 0
        ? Math.floor(base / step + 1e-9) * step + step
        : Math.ceil(base / step - 1e-9) * step - step;
    const next = roundQuantity(Math.max(min, grid), 3);
    onChange(next);
    setDraft(formatQuantity(next));
    inputRef.current?.focus();
  };

  return (
    <div
      className={cn(
        "inline-flex h-11 min-w-[8.75rem] items-stretch overflow-hidden rounded-xl border border-border bg-surface shadow-sm",
        className,
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-full w-11 shrink-0 rounded-none border-0 bg-transparent text-foreground/80 hover:bg-secondary/70 hover:text-foreground"
        onClick={() => applyStep(-step)}
        disabled={disabled}
        aria-label="Diminuir quantidade"
      >
        <Minus className="h-4 w-4" />
      </Button>
      <div className="flex min-w-0 flex-1 items-center justify-center border-x border-border/70 bg-transparent px-2">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => commit(e.currentTarget.value.trim())}
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp") {
              e.preventDefault();
              applyStep(step);
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              applyStep(-step);
            }
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
          onFocus={(e) => e.currentTarget.select()}
          className="h-full w-full min-w-0 border-0 bg-transparent px-0 text-center text-base font-semibold tabular-nums text-foreground outline-none placeholder:text-muted-foreground focus:outline-none focus:ring-0"
          aria-label="Quantidade"
        />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-full w-11 shrink-0 rounded-none border-0 bg-transparent text-foreground/80 hover:bg-secondary/70 hover:text-foreground"
        onClick={() => applyStep(step)}
        disabled={disabled}
        aria-label="Aumentar quantidade"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}
